import { ChangeEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppXBtn } from '@/UI/AppXBtn/AppXBtn';
import { TextIteratorIcon } from '@/UI/Icons';
import { Input } from '@/UI/Input/Input';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { useIsHovered } from '@/hooks/useIsHovered';
import { I18N_KEYS } from '@/language/keys';
import { useUserWorkflowRole } from '@/state/workflow.state';
import { getListSelectorIteratorMaxOptions } from '@/utils/listNode';
import { Dropdown } from '@/UI/Dropdown/Dropdown';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { AddItemButton } from '../Common/AddItemButton/AddItemButton';
import { useListSelectorView, useNode, useNodeData, useNodeStringArrayValue } from '../Recipe/FlowContext';
import { DynamicNode2 } from './DynamicNode/DynamicNode2';
import { LIST_MAX_CHARS } from './consts/validations.consts';
import type { ListSelectorData } from '@/types/node';
import type { NodeId } from 'web';

interface ListSelectorProps {
  id: string;
  data: ListSelectorData;
  disabled: boolean;
}

export function ListSelectorNode({ id }: ListSelectorProps) {
  const role = useUserWorkflowRole();
  const data = useNodeData<ListSelectorData>(id);
  const node = useNode(id);

  const isIterator = data.isIterator;
  const disabled = role !== 'editor' || node.locked;

  return (
    <DynamicNode2
      id={id}
      data={data}
      className="mux"
      size={isIterator ? 'medium' : 'small'}
      icon={isIterator ? <TextIteratorIcon /> : null}
      outputHandleYPos="30px"
      inputHandleYPos="30px"
    >
      {isIterator ? (
        <TextIteratorContent id={id} data={data} disabled={disabled} />
      ) : (
        <ListContent id={id} data={data} disabled={disabled} />
      )}
    </DynamicNode2>
  );
}

function ListContent({ id, data, disabled }: ListSelectorProps) {
  const { t } = useTranslation();
  const view = useListSelectorView(id);
  const dropdownOptions = useNodeStringArrayValue(id);

  const handleDropdownChange = useCallback(
    (option: { id: string }) => {
      const newIndex = Number(option.id);
      view.setSelected(newIndex);
    },
    [view],
  );

  return (
    <Dropdown
      width="100%"
      size="large"
      matchTriggerWidth
      options={dropdownOptions.map((option, index) => ({ id: String(index), label: option, value: option }))}
      value={dropdownOptions[data.selected]}
      onChange={handleDropdownChange}
      emptyState={
        dropdownOptions.length === 0
          ? t(I18N_KEYS.RECIPE_MAIN.NODES.LIST_NODE.ADD_OPTIONS_VIA_PROPERTIES_PANEL)
          : t(I18N_KEYS.RECIPE_MAIN.NODES.LIST_NODE.SELECT_AN_OPTION)
      }
      disabled={disabled}
      sx={{ pointerEvents: disabled ? 'none' : '' }}
    />
  );
}

function TextIteratorContent({
  id,
  data,
  disabled: disabledProp,
}: {
  id: NodeId;
  data: ListSelectorData;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const view = useListSelectorView(id);
  const hasStringInput = view.hasStringInput();
  const options = useNodeStringArrayValue(id);
  const hasInput = view.hasInput();
  const disabled = disabledProp || hasInput;
  const { delimiter } = data;
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const createRowId = useCallback(() => crypto.randomUUID(), []);
  const missingRowIdsRef = useRef<Record<number, string>>({});
  const [rowIds, setRowIds] = useState<string[]>(() => options.map(() => createRowId()));
  const [pendingFocusIndex, setPendingFocusIndex] = useState<number | null>(null);
  const [localDelimiter, setLocalDelimiter] = useState(delimiter);

  const getRowId = useCallback(
    (index: number) => {
      return rowIds[index] ?? (missingRowIdsRef.current[index] ??= createRowId());
    },
    [createRowId, rowIds],
  );

  useEffect(() => {
    setLocalDelimiter(delimiter);
  }, [delimiter]);

  useEffect(() => {
    // Reconcile rowIds only when the backing list length changes (i.e. options updated asynchronously).
    if (rowIds.length !== options.length) {
      setRowIds(Array.from({ length: options.length }, () => createRowId()));
      inputRefs.current = [];
      missingRowIdsRef.current = {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.length]);

  const handleValueChange = useCallback(
    (value: string, index: number) => {
      const newOptions = options.map((option, i) => (i === index ? value : option));
      view.setOptions(newOptions);
    },
    [view, options],
  );

  const handleAddItem = useCallback(() => {
    const newOptions = [...options, ''];
    setRowIds((prev) => [...prev, createRowId()]);
    view.setOptions(newOptions);
  }, [createRowId, view, options]);

  const handleEnter = useCallback(
    (index: number) => {
      if (options.length >= getListSelectorIteratorMaxOptions()) {
        return;
      }

      const newOptions = [...options.slice(0, index + 1), '', ...options.slice(index + 1)];
      setRowIds((prev) => {
        const next = [...prev];
        next.splice(index + 1, 0, createRowId());
        return next;
      });
      setPendingFocusIndex(index + 1);
      view.setOptions(newOptions);
    },
    [createRowId, view, options],
  );

  const handleTab = useCallback(
    (index: number, direction: 'next' | 'prev') => {
      if (options.length === 0) {
        return;
      }

      const jump = direction === 'next' ? 1 : -1;
      const nextIndex = (index + jump + options.length) % options.length;
      setPendingFocusIndex(nextIndex);
    },
    [options.length],
  );

  const handleDelete = useCallback(
    (index: number) => {
      const newOptions = options.filter((_, i) => i !== index);
      setRowIds((prev) => prev.filter((_, i) => i !== index));
      view.setOptions(newOptions);
    },
    [view, options],
  );

  const handleDeleteAndFocusPrev = useCallback(
    (index: number) => {
      const focusIndex = Math.max(0, index - 1);
      setPendingFocusIndex(focusIndex);
      handleDelete(index);
    },
    [handleDelete],
  );

  const setDelimiter = useCallback(
    (value: string) => {
      view.setDelimiter(value);
    },
    [view],
  );

  useLayoutEffect(() => {
    if (pendingFocusIndex == null) {
      return;
    }

    const el = inputRefs.current[pendingFocusIndex];
    if (el) {
      el.focus();
      el.select();
      setPendingFocusIndex(null);
    }
  }, [pendingFocusIndex, options]);

  return (
    <FlexCol sx={{ width: '100%', gap: 0.75, cursor: 'default' }}>
      {hasStringInput && (
        <FlexCenVer sx={{ gap: 1 }}>
          <TextSmRg text={t(I18N_KEYS.RECIPE_MAIN.NODES.ARRAY.SPLIT_TEXT_BY)} />
          <Input
            value={localDelimiter}
            onChange={(evt) => setLocalDelimiter(evt.target.value)}
            inputProps={{ maxLength: 10 }}
            onBlur={() => {
              if (localDelimiter !== '') {
                return setDelimiter(localDelimiter);
              }
              setLocalDelimiter(delimiter);
            }}
            onKeyDown={(evt) => {
              if (evt.key !== 'Enter') {
                if (evt.key === 'Escape') {
                  evt.preventDefault();
                  evt.stopPropagation();
                  setLocalDelimiter(delimiter);
                  (evt.currentTarget as HTMLInputElement).blur();
                }
                return;
              }

              evt.preventDefault();
              evt.stopPropagation();
              if (localDelimiter !== '') {
                setDelimiter(localDelimiter);
              } else {
                setLocalDelimiter(delimiter);
              }
              (evt.currentTarget as HTMLInputElement).blur();
            }}
            sx={{ width: 40 }}
          />
        </FlexCenVer>
      )}
      {options.map((option, index) => (
        <TextIteratorItem
          key={getRowId(index)}
          item={option}
          index={index}
          onValueChange={handleValueChange}
          onEnter={handleEnter}
          onTab={handleTab}
          inputRef={(el) => {
            inputRefs.current[index] = el;
          }}
          onDelete={handleDelete}
          onDeleteAndFocusPrev={handleDeleteAndFocusPrev}
          editable={!disabled}
          disableDelete={options.length === 1}
        />
      ))}
      {!disabled && (
        <div>
          <AddItemButton
            show
            onClick={() => handleAddItem()}
            text={t(I18N_KEYS.RECIPE_MAIN.NODES.LIST_NODE.ITERATOR.ADD_ITEM)}
            disabled={options.length >= getListSelectorIteratorMaxOptions()}
          />
        </div>
      )}
    </FlexCol>
  );
}

function TextIteratorItem({
  editable = false,
  item,
  index,
  onValueChange,
  onEnter,
  onTab,
  inputRef,
  onDelete,
  onDeleteAndFocusPrev,
  disableDelete = false,
}: {
  editable?: boolean;
  item: string;
  index: number;
  onValueChange: (value: string, index: number) => void;
  onEnter: (index: number) => void;
  onTab: (index: number, direction: 'next' | 'prev') => void;
  inputRef: (el: HTMLInputElement | null) => void;
  onDelete: (index: number) => void;
  onDeleteAndFocusPrev: (index: number) => void;
  disableDelete?: boolean;
}) {
  const { t } = useTranslation();
  const { isHovered, ...hoverProps } = useIsHovered();
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => onValueChange(evt.target.value, index),
    [index, onValueChange],
  );
  const handleDelete = useCallback(() => onDelete(index), [index, onDelete]);

  const handleKeyDown = useCallback(
    (evt: React.KeyboardEvent<HTMLInputElement>) => {
      if (!editable) {
        return;
      }

      if (evt.key === 'Escape') {
        evt.preventDefault();
        evt.stopPropagation();
        (evt.currentTarget as HTMLInputElement).blur();
        return;
      }

      if (evt.key === 'Tab') {
        evt.preventDefault();
        evt.stopPropagation();
        return onTab(index, evt.shiftKey ? 'prev' : 'next');
      }

      if (evt.key === 'Enter') {
        evt.preventDefault();
        evt.stopPropagation();

        if (item.length === 0) {
          return;
        }
        return onEnter(index);
      }

      if (evt.key === 'Backspace' && item.length === 0 && !disableDelete) {
        evt.preventDefault();
        evt.stopPropagation();
        onDeleteAndFocusPrev(index);
      }
    },
    [disableDelete, editable, index, item, onDeleteAndFocusPrev, onEnter, onTab],
  );

  return (
    <FlexCenVer sx={{ width: '100%', height: 32 }} {...hoverProps} className={isFocused ? 'nodrag' : ''}>
      <Input
        fullWidth
        disabled={!editable}
        value={item}
        onChange={handleChange}
        inputProps={{ maxLength: LIST_MAX_CHARS }}
        onKeyDown={handleKeyDown}
        noBlurOnEnter
        placeholder={t(I18N_KEYS.RECIPE_MAIN.NODES.LIST_NODE.ITERATOR.PLACEHOLDER, { index: index + 1 })}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        sx={{ pointerEvents: !editable ? 'none' : '' }}
        inputRef={inputRef}
      />
      {editable && isHovered ? (
        <AppXBtn disabled={disableDelete} onClick={handleDelete} size={20} sx={{ ml: 0.5 }} />
      ) : null}
    </FlexCenVer>
  );
}
